// ============================================================================
// UserMenu — the avatar dropdown. CW-J Phase 2.
//
// Before this the avatar was decorative: a coloured circle with no click
// handler, and "Sign out" sat beside it as its own button in the header. Ben's
// gate decision put the theme control "in the avatar menu", so the menu had to
// exist. Sign out moves into it; the header keeps one control instead of two.
//
// Dismissal uses overlayClickClose (AFB-0042) rather than onClick, so a text
// selection that ends over the backdrop does not close the menu.
// ============================================================================

import { useEffect, useRef, useState } from 'react';
import { BellRing, LogOut, Monitor, Moon, Sun } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Avatar } from './components';
import { overlayClickClose } from './overlay-close';
import { canChooseTheme, useTheme, type ThemeChoice } from './theme';
import { useAuth } from './auth';

const CHOICES: { value: ThemeChoice; label: string; Icon: typeof Sun }[] = [
  { value: 'system', label: 'System', Icon: Monitor },
  { value: 'light', label: 'Light', Icon: Sun },
  { value: 'dark', label: 'Dark', Icon: Moon },
];

export function UserMenu({
  fullName,
  role,
  email,
  onSignOut,
}: {
  fullName: string;
  role: string | null;
  email: string | null | undefined;
  onSignOut: () => void;
}) {
  const [open, setOpen] = useState(false);
  const { choice, setChoice } = useTheme();
  const { employee } = useAuth();
  const showAppearance = canChooseTheme(employee?.employee_id);
  const btnRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        btnRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Account menu"
        className="flex min-h-[44px] items-center gap-3 rounded-lg px-1.5 hover:bg-tcs-surface focus:outline-none focus-visible:ring-2 focus-visible:ring-tcs-accent"
      >
        <div className="hidden text-right sm:block">
          <div className="text-sm font-medium leading-tight text-tcs-text">{fullName}</div>
          <div className="font-mono text-[10px] uppercase tracking-wide text-tcs-text-muted">
            {role ?? 'No role'} · {email}
          </div>
        </div>
        <Avatar name={fullName} />
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[60]"
          {...overlayClickClose(() => setOpen(false))}
          aria-hidden="true"
        >
          <div
            className="absolute right-3 top-14 w-64 rounded-xl border border-tcs-border bg-tcs-overlay p-1.5 shadow-xl md:right-6"
            role="menu"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-2.5 py-2">
              <div className="truncate text-sm font-semibold text-tcs-text">{fullName}</div>
              <div className="truncate font-mono text-[10px] uppercase tracking-wide text-tcs-text-muted">
                {role ?? 'No role'} · {email}
              </div>
            </div>

            {showAppearance && (
              <>
                <div className="mx-1 my-1.5 h-px bg-tcs-border" />
                <div className="px-2.5 pb-1 pt-1 text-[10px] font-bold uppercase tracking-wider text-tcs-text-muted">
                  Appearance
                </div>
                <div
                  className="mx-1 mb-1.5 flex gap-1 rounded-lg border border-tcs-border bg-tcs-bg-muted p-1"
                  role="radiogroup"
                  aria-label="Appearance"
                >
                  {CHOICES.map(({ value, label, Icon }) => {
                    const on = choice === value;
                    return (
                      <button
                        key={value}
                        type="button"
                        role="radio"
                        aria-checked={on}
                        onClick={() => setChoice(value)}
                        className={
                          'flex min-h-[36px] flex-1 items-center justify-center gap-1.5 rounded-md text-xs font-semibold transition-colors ' +
                          (on
                            ? 'bg-tcs-inverse text-tcs-inverse-ink'
                            : 'text-tcs-text-secondary hover:bg-tcs-surface')
                        }
                      >
                        <Icon className="h-3.5 w-3.5" aria-hidden />
                        {label}
                      </button>
                    );
                  })}
                </div>
              </>
            )}

            {/* Ben, 7 Oct 2026: notification settings live here, not as a sidebar row. */}
            <div className="mx-1 my-1.5 h-px bg-tcs-border" />
            <Link
              to="/my-work/notifications"
              role="menuitem"
              onClick={() => setOpen(false)}
              className="flex min-h-[44px] w-full items-center gap-2 rounded-lg px-2.5 text-sm font-medium text-tcs-text hover:bg-tcs-surface"
            >
              <BellRing className="h-3.5 w-3.5" aria-hidden />
              Notification settings
            </Link>
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                onSignOut();
              }}
              className="flex min-h-[44px] w-full items-center gap-2 rounded-lg px-2.5 text-sm font-medium text-tcs-text hover:bg-tcs-surface"
            >
              <LogOut className="h-3.5 w-3.5" aria-hidden />
              Sign out
            </button>
          </div>
        </div>
      )}
    </>
  );
}
